import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
    selector: 'app-typeahead-dropdown',
    templateUrl: './typeahead-dropdown.component.html',
    styleUrls: ['./typeahead-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TypeaheadDropdownComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TypeaheadDropdownComponent<T extends Object> implements ControlValueAccessor, OnInit {
  @Input() label: string;
  @Input() placeholder: string;
  @Input() set options(value: T[]) {
    this.optionsSubject.next(value);
  }
  @Input() valueKey: keyof T;
  @Input() labelKey: keyof T;
  @Input() showDefaultOption: boolean = true;
  @Input() hideClearButton: boolean;

  @Output() onChangeEvent: EventEmitter<T> = new EventEmitter<T>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTriggerRef: MatAutocompleteTrigger;
  @ViewChild('autocompleteTrigger', { read: ElementRef }) autocompleteInputRef: ElementRef<HTMLInputElement>;

  isDisabled: boolean = false;
  defaultOption = {
    value: null,
    label: '-- Use default owner field --',
  }
  options$: Observable<T[]>;

  readonly filterInputControl: FormControl<T[keyof T] | string | null> = new FormControl<T[keyof T] | string | null>(null);

  private readonly optionsSubject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  private onChanged: (value: T[keyof T]) => void;
  private onTouched: () => void;

  constructor(private readonly cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.options$ = this.optionsSubject.asObservable();
  }

  onSelect(item?: T): void {
    if (this.onChanged) {
      this.onChanged((item?.[this.valueKey] ?? null) as T[keyof T]);
      this.onChangeEvent.emit(item);
    }
  }

  onClear(): void {
    this.filterInputControl.setValue(null);
    this.onSelect();
  }

  onChevronClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isDisabled || !this.autocompleteTriggerRef) {
      return;
    }

    if (this.autocompleteTriggerRef.panelOpen) {
      this.autocompleteTriggerRef.closePanel();
      return;
    }

    this.autocompleteInputRef?.nativeElement.focus();
    this.autocompleteTriggerRef.openPanel();
  }

  registerOnChange(fn: (value: T[keyof T]) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: T[keyof T] | null): void {
    this.filterInputControl.setValue(value ?? null);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (isDisabled) {
      this.filterInputControl.disable();
      return;
    }

    this.filterInputControl.enable();
  }

  getTitle(value: T[keyof T] | string | null): string {
    const options: T[] = this.optionsSubject.getValue();
    const normalizedValue = value === null || value === undefined || value === '' ? null : value;

    this.cdr.markForCheck();

    if (this.showDefaultOption && this.defaultOption.value === normalizedValue) {
      return this.defaultOption.label;
    }

    const found = options?.find((option: T) => option[this.valueKey] === (normalizedValue as T[keyof T]));

    return found ? String(found[this.labelKey] ?? '') : '';
  }
}
